import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { Button, Box, useTheme, useMediaQuery } from '@mui/material';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DatabaseGrid from './DatabaseGrid';
import StarHeader from './../../../../../../../public/assets/icons/star_header.png';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import { getAllPeopleForTasksAsync } from '@application/features/people/peopleThunk';
import { useAppDispatch } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { assignTicketsManuallyAsync } from '@application/features/tickets/ticketsThunk';
import { toast } from 'react-toastify';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import CustomDialog from '@ui/components/common/Dialog';
const AssignDialog = ({ open, onClose, type, ticketId, onConfirm }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const dispatch = useAppDispatch();
    const { selectedCompanyId } = useSelector((state) => state.company);
    const { peopleForTasks } = useSelector((state) => state.people);
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            company: '',
            referredBy: ''
        }
    });
    const onSubmit = (values) => {
        // if (onConfirm) onConfirm(values);
        if (type === 'Manual' && ticketId) {
            const request = {
                ticketId: ticketId,
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                email: values.email,
                phone: values.phone,
                company: values.company ? values.company : undefined,
                referredBy: values.referredBy ? values.referredBy : undefined
            };
            dispatch(assignTicketsManuallyAsync(request))
                .unwrap()
                .then((response) => {
                if (response.isSuccess) {
                    toast.success(response.message || 'Ticket has successfully been assigned.');
                }
                else {
                    toast.error(response.message || 'Ticket was not found.');
                }
            })
                .catch((error) => {
                toast.error('Failed to assign ticket.');
            });
        }
        reset();
        onClose();
    };
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            dispatch(getAllPeopleForTasksAsync());
        }
    }, [selectedCompanyId]);
    const referredByOptions = peopleForTasks?.map((people) => ({
        label: `${people.name} (${people.peopleCompany})`,
        value: people.id
    })) ?? [];
    // Manual form content
    const manualFormContent = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', name: 'firstName', label: t('sponsorships.tickets.assign.firstName'), control: control, rules: {
                    required: t('validations.required', {
                        field: t('sponsorships.tickets.assign.firstName')
                    })
                }, errorMessage: errors.firstName?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'lastName', label: t('sponsorships.tickets.assign.lastName'), control: control, rules: {
                    required: t('validations.required', {
                        field: t('sponsorships.tickets.assign.lastName')
                    })
                }, errorMessage: errors.lastName?.message }), _jsx(PhoneInputMasking, { bg: 'grey8', control: control, label: t('sponsorships.tickets.assign.phone'), name: 'phone', error: errors.phone?.message, rules: {
                    required: t('validations.required', {
                        field: t('sponsorships.tickets.assign.phone')
                    }),
                    validate: (value) => {
                        // Accepts only numbers, must be at least 8 digits
                        const phoneRegex = /^\d{8,}$/;
                        if (!phoneRegex.test(value || '')) {
                            return t('validations.invalidPhone', {
                                field: t('sponsorships.tickets.assign.phone')
                            });
                        }
                        return true;
                    }
                } }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'email', label: t('sponsorships.tickets.assign.email'), control: control, rules: {
                    required: t('validations.required', {
                        field: t('sponsorships.tickets.assign.email')
                    })
                }, errorMessage: errors.email?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'company', label: t('sponsorships.tickets.assign.companyOptional'), control: control, errorMessage: errors.company?.message }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'referredBy', label: t('sponsorships.tickets.assign.referralOptional'), options: referredByOptions, error: errors.referredBy?.message })] }));
    // Database type - use CustomDialog with lg maxWidth
    if (type === 'Database') {
        return (_jsx(CustomDialog, { backgroundVariant: 'white', open: open, onClose: onClose, titleWithIcon: t('sponsorships.tickets.assign.assignTickets'), titleIcon: StarHeader, maxWidth: 'lg', actions: _jsx(Box, { sx: { display: 'flex', gap: 1.5, width: '100%' }, children: _jsx(Button, { fullWidth: true, onClick: onClose, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }) }), children: _jsx(Box, { p: 1.5, children: _jsx(DatabaseGrid, { ticketId: ticketId, onClose: onClose }) }) }));
    }
    // Manual type - use CustomDialog with legacy styling
    return (_jsx(CustomDialog, { backgroundVariant: 'white', open: open, onClose: onClose, titleWithIcon: t('sponsorships.tickets.assign.assignTickets'), titleIcon: StarHeader, maxWidth: 'sm', actions: _jsxs(Box, { sx: { display: 'flex', gap: 1.5, width: '100%' }, children: [_jsx(Button, { fullWidth: true, onClick: onClose, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: handleSubmit(onSubmit), children: t('sponsorships.tickets.assignLabel') })] }), children: _jsx(Box, { p: 1.5, sx: isDesktop
                ? {
                    maxHeight: '65vh',
                    overflowY: 'auto'
                }
                : undefined, children: manualFormContent }) }));
};
export default AssignDialog;
